import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { RequestService } from '../../../../services/request.service';
import { SnackService } from '../../../../services/snack.service';
import { FormatDecimal } from '../../../../pipes/format-decimal';
import { SubscriptionBasedComponent } from '../../../../subscription-helpers/subscription-based.component';
import { SignSize } from '../../../../model/product/board/sign-size';
import { SignConstructionRequirement } from '../../../../model/product/board/sign-construction-requirement';
import Material from '../../../../model/material/material';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import {
  BoardTypeChoiceArray,
  PlacementTypeChoiceArray,
} from '../../../../model/product/board/board-part';
import {
  SignConstructionMaterialType,
  SignConstructionMaterialTypeChoiceArray,
  SupportedLengthsByMaterialTypeOrdinal,
} from '../../../../model/product/board/sign-construction-material-type';
import BoardType from '../../../../model/product/board/board-type';
import PlacementType from '../../../../model/product/board/placement-type';
import { cloneDeep } from 'lodash';
import {
  MaterialChoices,
  MaterialChoicesArray,
} from '../../../../model/product/board/board';
import { Subject } from 'rxjs';
import { DEFAULT_DEBOUNCE_TIME } from 'src/globals/globals';
import { PRODUCTS } from '../../../../helpers/constants';

@Component({
  selector: 'app-sign-size-edit',
  templateUrl: './sign-size-edit.component.html',
  styleUrls: ['./sign-size-edit.component.scss'],
  providers: [FormatDecimal],
})
export class SignSizeEditComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  productSize: SignSize;

  boardTypeChoices: BoardType[];
  placementTypeChoices: PlacementType[];
  constructionMaterialTypeChoices: SignConstructionMaterialType[];
  materialTypeChoices: Material[];
  supportedLengthsByConstructionMaterialTypeOrdinal: Map<number, Set<number>>;

  sumDisplay: string;
  retailSumDisplay: string;
  editTitle: string;

  productionPriceSubject = new Subject<number>();
  productionRetailPriceSubject = new Subject<number>();
  placementPriceSubject = new Subject<number>();
  placementRetailPriceSubject = new Subject<number>();
  paintedPostsPriceSubject = new Subject<number>();
  transportPriceSubject = new Subject<number>();
  removalPriceSubject = new Subject<number>();
  recyclePriceSubject = new Subject<number>();

  @Output()
  saveSuccessEvent = new EventEmitter<SignSize>();

  readonly baseUrl: string = '/sign_sizes';

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    super();
    let categoryTitle = PRODUCTS.PROJECTBORDEN;
    this.editTitle = categoryTitle.substring(0, categoryTitle.length - 2); //remove 'en' plural suffix
    this.productSize = data.productSize; //Also not a fan of this being required... automate this without needing injections
  }

  ngOnInit() {
    this.boardTypeChoices = BoardTypeChoiceArray;
    this.placementTypeChoices = PlacementTypeChoiceArray;
    this.constructionMaterialTypeChoices =
      SignConstructionMaterialTypeChoiceArray.slice();
    this.constructionMaterialTypeChoices.push(null);
    this.materialTypeChoices = cloneDeep(MaterialChoicesArray);
    this.materialTypeChoices = this.materialTypeChoices.filter(
      (i) => i.ordinal !== MaterialChoices.UNSPECIFIED.ordinal
    );
    this.supportedLengthsByConstructionMaterialTypeOrdinal =
      SupportedLengthsByMaterialTypeOrdinal;
    this.computeAndSetSum();
    this.computeAndSetRetailSum();
    this.addSubscription(
      this.productionPriceSubject,
      (value) => (this.productSize.productionPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.productionRetailPriceSubject,
      (value) => (this.productSize.productionRetailPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.placementPriceSubject,
      (value) => (this.productSize.placementPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.placementRetailPriceSubject,
      (value) => (this.productSize.placementRetailPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.paintedPostsPriceSubject,
      (value) => (this.productSize.paintedPostsPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.transportPriceSubject,
      (value) => (this.productSize.transportPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.removalPriceSubject,
      (value) => (this.productSize.removalPrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.recyclePriceSubject,
      (value) => (this.productSize.recyclePrice = value),
      () => {},
      DEFAULT_DEBOUNCE_TIME
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.productionPriceSubject.complete();
    this.productionRetailPriceSubject.complete();
    this.placementPriceSubject.complete();
    this.placementRetailPriceSubject.complete();
    this.paintedPostsPriceSubject.complete();
    this.transportPriceSubject.complete();
    this.removalPriceSubject.complete();
    this.recyclePriceSubject.complete();
  }

  save() {
    const canSave = this.checkSignSize(this.productSize);
    if (!canSave) {
      return;
    }
    if (this.productSize.id != null) {
      this.addSubscription(
        this.requestService.put(this.productSize, this.baseUrl),
        (result) => {
          this.saveSuccessEvent.emit(result);
          this.snackService.showSuccess('Bord succesvol opgeslagen');
        }
      );
    } else {
      this.addSubscription(
        this.requestService.post(this.productSize, this.baseUrl),
        (result) => {
          this.saveSuccessEvent.emit(result);
          this.snackService.showSuccess('Bord succesvol opgeslagen');
        }
      );
    }
  }

  checkSignSize(size: SignSize): boolean {
    if (size.material == null) {
      this.snackService.showError('Gelieve een materiaal te specifiëren');
      return false;
    }
    if (size.width == null) {
      this.snackService.showError('Gelieve een breedte te specifiëren');
      return false;
    }
    if (size.height == null) {
      this.snackService.showError('Gelieve een hoogte te specifiëren');
      return false;
    }
    if (size.productionPrice == null) {
      this.snackService.showError('Gelieve een aanmaakprijs te specifiëren');
      return false;
    }
    if (size.placementPrice == null) {
      this.snackService.showError('Gelieve een plaatsingsprijs te specifiëren');
      return false;
    }
    if (size.transportPrice == null) {
      this.snackService.showError('Gelieve een transportprijs te specifiëren');
      return false;
    }
    if (size.removalPrice == null) {
      this.snackService.showError('Gelieve een weghaalprijs te specifiëren');
      return false;
    }
    if (size.paintedPostsPrice == null) {
      this.snackService.showError(
        'Gelieve een geschilderde palen prijs te specifiëren'
      );
      return false;
    }
    if (size.recyclePrice == null) {
      this.snackService.showError('Gelieve een recycleerprijs te specifiëren');
      return false;
    }
    if (size.placementType == null) {
      this.snackService.showError('Gelieve een plaatsingstype te specifiëren');
      return false;
    }
    if (size.boardType == null) {
      this.snackService.showError('Gelieve een bordtype te specifiëren');
      return false;
    }
    if (size.nbInstallersRequired == null) {
      this.snackService.showError(
        'Gelieve het aantal installateurs te specifiëren'
      );
      return false;
    }
    if (size.cmToBottom == null) {
      this.snackService.showError(
        'Gelieve afstand tot onderkant bord te specifiëren'
      );
      return false;
    }
    if (size.removalMinutes == null) {
      this.snackService.showError('Gelieve tijd weghalen te specifiëren');
      return false;
    }
    if (size.requiredConstructionMaterials.length === 0) {
      this.snackService.showError(
        'Gelieve minstens één constructiemateriaal te specifiëren'
      );
      return false;
    }
    for (const constructionMaterial of size.requiredConstructionMaterials) {
      if (!this.checkConstructionRequirement(constructionMaterial)) {
        return false;
      }
    }

    return true;
  }

  private checkConstructionRequirement(
    requirement: SignConstructionRequirement
  ): boolean {
    if (requirement.signConstructionMaterialType == null) {
      if (requirement.description == null) {
        this.snackService.showError(
          'Constructie: gelieve ofwel een constructiemateriaal type, ofwel een beschrijving te specifiëren'
        );
        return false;
      }
    } else {
      const supportedMaterialLengths =
        this.supportedLengthsByConstructionMaterialTypeOrdinal.get(
          requirement.signConstructionMaterialType.ordinal
        );
      if (
        supportedMaterialLengths != null &&
        (requirement.signConstructionMaterialLength == null ||
          !supportedMaterialLengths.has(
            requirement.signConstructionMaterialLength
          ))
      ) {
        this.snackService.showError(
          'Constructie: gelieve de lengte te specifiëren'
        );
        return false;
      }
    }

    if (requirement.amount == null) {
      this.snackService.showError(
        'Constructie: gelieve een aantal te specifiëren'
      );
      return false;
    }

    return true;
  }

  compareMaterials(m1: Material, m2: Material): boolean {
    if (m1 == null && m2 == null) {
      return true;
    } else if (m1 != null && m2 != null) {
      if (m1.ordinal === m2.ordinal) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  compareBoardType(b1: BoardType, b2: BoardType): boolean {
    if (b1 == null && b2 == null) {
      return true;
    } else if (b1 != null && b2 != null) {
      if (b1.ordinal === b2.ordinal) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  comparePlacementType(b1: PlacementType, b2: PlacementType): boolean {
    if (b1 == null && b2 == null) {
      return true;
    } else if (b1 != null && b2 != null) {
      if (b1.ordinal === b2.ordinal) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  compareConstructionMaterialType(
    b1: SignConstructionMaterialType,
    b2: SignConstructionMaterialType
  ): boolean {
    if (b1 == null && b2 == null) {
      return true;
    } else if (b1 != null && b2 != null) {
      if (b1.ordinal === b2.ordinal) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  private stringToNumber(input: string): number {
    if (typeof input === 'number') return input;
    return Number(input.replace(/\./g, '').replace(/,/, '.'));
  }

  computeAndSetSum(): void {
    this.sumDisplay = this.computedSum(
      this.productSize.productionPrice,
      this.productSize.placementPrice
    );
  }

  computeAndSetRetailSum(): void {
    const productionPrice = this.isNullOrEmpty(
      this.productSize.productionRetailPrice
    )
      ? this.productSize.productionPrice
      : this.productSize.productionRetailPrice;
    const placementPrice = this.isNullOrEmpty(
      this.productSize.placementRetailPrice
    )
      ? this.productSize.placementPrice
      : this.productSize.placementRetailPrice;

    this.retailSumDisplay = this.computedSum(productionPrice, placementPrice);
  }

  private isNullOrEmpty(s: string): boolean {
    return s == null || s === '';
  }

  private computedSum(productionPrice: string, placementPrice: string): string {
    let sum: number = 0;
    if (!this.isNullOrZero(productionPrice)) {
      sum += this.stringToNumber(productionPrice);
    }
    if (!this.isNullOrZero(placementPrice)) {
      sum += this.stringToNumber(placementPrice);
    }

    return sum.toString();
  }

  private isNullOrZero(s: string): boolean {
    return s == null || +s == 0;
  }

  addConstructionMaterial(): void {
    this.productSize.requiredConstructionMaterials.push(
      new SignConstructionRequirement()
    );
  }

  removeMaterial(index: number): void {
    this.productSize.requiredConstructionMaterials.splice(index, 1);
  }

  getConstructionMaterialTypeLengthChoices(
    type: SignConstructionMaterialType
  ): Set<number> {
    return this.supportedLengthsByConstructionMaterialTypeOrdinal.get(
      type.ordinal
    );
  }

  onProductionPriceChange(): void {
    this.computeAndSetSum();
    this.computeAndSetRetailSum();
  }

  onProductionRetailPriceChange(): void {
    this.computeAndSetRetailSum();
  }

  onPlacementPriceChange(): void {
    this.computeAndSetSum();
    this.computeAndSetRetailSum();
  }

  onPlacementRetailPriceChange(): void {
    this.computeAndSetRetailSum();
  }

  onPaintedPostsPriceChange(): void {
    this.computeAndSetSum();
    this.computeAndSetRetailSum();
  }

  protected readonly PRODUCTS = PRODUCTS;
}
